import { useState } from 'react';
import { Copy, Plus, Check } from 'lucide-react';
import { InvitationCode } from '../../types';

// Mock invitation codes
const mockInvites: InvitationCode[] = [
  {
    code: 'CREAI2025',
    creator: 'admin',
    createdAt: new Date().toISOString(),
    status: 'unused',
  },
  {
    code: 'DEMO2024',
    creator: 'admin',
    createdAt: '2024-03-01T10:00:00Z',
    status: 'unused',
  },
  {
    code: 'BETA2024',
    creator: 'admin',
    createdAt: '2024-02-15T14:30:00Z',
    status: 'used',
    usedBy: 'john_doe',
  },
  {
    code: 'TEST2024',
    creator: 'admin',
    createdAt: '2024-03-10T09:15:00Z',
    status: 'unused',
  },
];

export function InviteManagement() {
  const [invites, setInvites] = useState(mockInvites);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [batchSize, setBatchSize] = useState(1);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  const handleCreateInvites = () => {
    // Mock invite creation
    const newInvites = [...Array(batchSize)].map((_, index) => ({
      code: `INVITE${Date.now()}${index}`,
      creator: 'admin',
      createdAt: new Date().toISOString(),
      status: 'unused' as const,
    }));

    setInvites([...newInvites, ...invites]);
    setShowCreateModal(false);
  };

  const handleCopyCode = async (code: string) => {
    await navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  return (
    <div>
      <div className="mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-[--text-primary]">
              Invitation Codes
            </h1>
            <p className="mt-2 text-sm text-[--text-secondary]">
              Manage and generate invitation codes for new users
            </p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <Plus className="w-5 h-5 mr-2" />
            Create Invites
          </button>
        </div>
      </div>

      {/* Invitation Codes Table */}
      <div className="bg-[--bg-primary] shadow overflow-hidden sm:rounded-lg">
        <table className="min-w-full divide-y divide-[--border-primary]">
          <thead className="bg-[--bg-secondary]">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-[--text-secondary] uppercase tracking-wider">
                Code
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-[--text-secondary] uppercase tracking-wider">
                Created By
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-[--text-secondary] uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-[--text-secondary] uppercase tracking-wider">
                Created At
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-[--text-secondary] uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-[--bg-primary] divide-y divide-[--border-primary]">
            {invites.map((invite) => (
              <tr key={invite.code}>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-[--text-primary]">
                  {invite.code}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-[--text-secondary]">
                  {invite.creator}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      invite.status === 'unused'
                        ? 'bg-success-100 text-success-800 dark:bg-[rgb(var(--success-900)/0.2)] dark:text-success-200'
                        : 'bg-[--bg-tertiary] text-[--text-primary]'
                    }`}
                  >
                    {invite.status}
                    {invite.usedBy && ` by ${invite.usedBy}`}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-[--text-secondary]">
                  {new Date(invite.createdAt).toLocaleString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  {invite.status === 'unused' && (
                    <button
                      onClick={() => handleCopyCode(invite.code)}
                      className="text-[--link-default] hover:text-[--primary-900] dark:text-[--primary-400] dark:hover:text-[--primary-300]"
                    >
                      {copiedCode === invite.code ? (
                        <Check className="w-5 h-5" />
                      ) : (
                        <Copy className="w-5 h-5" />
                      )}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create Invites Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-[rgb(var(--color-black-rgb)/0.5)] flex items-center justify-center">
          <div className="bg-[--bg-primary] rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-medium text-[--text-primary] mb-4">
              Create Invitation Codes
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[--text-primary]">
                  Number of Codes
                </label>
                <input
                  type="number"
                  min="1"
                  max="100"
                  value={batchSize}
                  onChange={(e) => setBatchSize(parseInt(e.target.value))}
                  className="mt-1 block w-full border border-[--border-secondary] rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 bg-[--bg-primary]"
                />
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 text-sm font-medium text-[--text-primary] hover:bg-[--state-hover] rounded-md"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateInvites}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md"
                >
                  Create
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}